<template>
  <div v-loading.fullscreen.lock="display.isLoading">
    <div class="product" v-if="product">
      <el-row :gutter="20">
        <el-col :xs="24" :md="14">
          <div v-if="product.detailGalleryImageUrls">
            <VueSlickCarousel arrows dots adaptiveHeight>
              <el-image
                :src="img"
                v-for="img in product.detailGalleryImageUrls"
                :key="img"
              />
            </VueSlickCarousel>
          </div>
        </el-col>
        <el-col :xs="24" :md="10">
          <div class="product__detail">
            <h3>{{ product.name }}</h3>
            <p>{{ product.sku }}</p>
            <p style="white-space: pre-wrap" v-if="product.description">
              {{ product.description }}
            </p>
            <p>{{ product.unitNetPriceDisplay }}</p>
            <div class="complex-product-details">
              <el-tabs v-model="display.packageOptions.currentTab">
                <el-tab-pane
                  v-if="product.package && product.package.isPackage"
                  label="Selections"
                  name="Selections"
                >
                  <div
                    class="side-dialog-selection"
                    v-for="(
                      lineItemSelection, lineItemSelectionIndex
                    ) in product.packageSelections"
                    :key="lineItemSelectionIndex"
                  >
                    <h3>
                      <strong>Variant {{ lineItemSelectionIndex + 1 }}</strong>
                    </h3>
                    <div
                      v-for="(
                        productSelection, productSelectionIndex
                      ) in product.package.packageOptions"
                      :key="productSelectionIndex"
                    >
                      <p>
                        <strong>{{ productSelection.name }}</strong>
                      </p>
                      <el-checkbox-group
                        v-model="lineItemSelection[productSelection.name]"
                      >
                        <el-checkbox
                          v-for="productOption in productSelection.options"
                          :key="productOption"
                          :label="productOption"
                          :disabled="
                            isSelectionDisabled(
                              productSelection,
                              productOption,
                              lineItemSelection
                            )
                          "
                        ></el-checkbox>
                      </el-checkbox-group>
                    </div>
                  </div>
                </el-tab-pane>
                <el-tab-pane
                  v-if="
                    product.specification &&
                    product.specification.isSpecificationEnabled
                  "
                  label="Specifications"
                  name="Specifications"
                >
                  <div class="side-dialog-selection">
                    <div
                      v-for="(sel, selIndex) in product.specification
                        .specificationOptions"
                      :key="selIndex"
                    >
                      <el-radio-group
                        v-model="product.specificationSelectionIndex"
                      >
                        <el-radio :label="selIndex">
                          {{ sel.name }} (${{ sel.unitNetPriceDisplay }})
                        </el-radio>
                      </el-radio-group>
                    </div>
                  </div>
                </el-tab-pane>
                <el-tab-pane
                  v-if="product.subProducts && product.subProducts.length > 0"
                  label="Sub Menu"
                  name="Sub Menu"
                >
                  <div class="side-dialog-selection">
                    <el-checkbox-group
                      v-model="product.selectedSubProductNames"
                    >
                      <el-checkbox
                        v-for="subProduct in product.subProducts"
                        :key="subProduct._id"
                        :label="`${subProduct.name} (${subProduct.unitNetPriceDisplay})`"
                      ></el-checkbox>
                    </el-checkbox-group>
                  </div>
                </el-tab-pane>
              </el-tabs>
            </div>
            <div
              v-if="!product.isSoldOut"
              class="product-list__item__shopping-cart-input"
            >
              <el-input-number
                v-if="product.isShowItemNumberInputInDetailPage"
                class="cart"
                v-model="product.shoppingCartItemQuantity"
                :min="0"
                :max="product.package.isPackage ? 1 : 1024"
                @change="onChangeShoppingCartItemQuantity"
              ></el-input-number>
              <el-button
                v-if="product.isEventProduct"
                class="bg-black gold"
                @click="() => onClickSaveDetailProductToCart(true)"
              >
                Add and Checkout
              </el-button>
              <el-button
                v-else
                class="bg-black gold"
                @click="onClickSaveDetailProductToCart"
              >
                Save to Cart
              </el-button>
            </div>
            <strong v-else>Sold Out</strong>
          </div>

          <div class="centre back-button-wrapper">
            <el-button class="bg-gold black" @click="goHome"
              >Back to Home</el-button
            >
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- Bottom bar -->
    <div class="bottom-bar bg-black">
      <el-row type="flex" align="middle">
        <el-col :span="12" class="white bottom-bar__col">
          <span v-if="shoppingCart.shoppingCart.lineItems">
            {{ shoppingCart.shoppingCart.lineItems.length }} items, ${{
              subtotal
            }}
          </span>
          <br />
          <delivery-indicator
            v-if="isDelivery(currentOrderMode)"
            :subtotal="Number(subtotal)"
            color="#eee"
            @clickInfo="
              display.dialog.minimumDeliveryAmountRule.isVisible = true
            "
          ></delivery-indicator>
        </el-col>
        <el-col :span="12" class="right bottom-bar__col">
          <el-button-group>
            <el-button
              size="small"
              class="bg-gold black border-black no-border-radius"
              @click="onClickShoppingCart"
              >Cart</el-button
            >
            <el-button
              size="small"
              class="bg-gold black border-black no-border-radius"
              @click="onClickCheckout"
              >Checkout</el-button
            >
          </el-button-group>
        </el-col>
      </el-row>
    </div>

    <!-- Minimum delivery amount rules dialog -->
    <delivery-rule-dialog
      :isVisible="display.dialog.minimumDeliveryAmountRule.isVisible"
      @close="display.dialog.minimumDeliveryAmountRule.isVisible = false"
    ></delivery-rule-dialog>
  </div>
</template>

<script>
import VueSlickCarousel from "vue-slick-carousel";
import "vue-slick-carousel/dist/vue-slick-carousel.css";
import "vue-slick-carousel/dist/vue-slick-carousel-theme.css";
import Cookies from "js-cookie";
import { mapGetters } from "vuex";
import moment from "moment";
import logger from "@/utils/logger";
import { getById } from "@/api/product";
import { decorateProduct } from "@/utils/product";
import { decorateShoppingCartItem } from "@/utils/shoppingCart";
import { MINIMUM_DELIVERY_AMOUNT, isDelivery } from "@/utils/order";
import { errorMessages } from "@/utils/info";
import {
  calculateSubtotal,
  getAttributeValue,
  setAttributeValue,
  deepClone,
  isNotNilOrEmpty,
} from "@/utils";

const name = "Product";
const log = logger(name);

export default {
  name,
  data() {
    return {
      isDelivery,

      display: {
        isLoading: true,
        dialog: {
          packageOptions: { isVisible: false },
          specifications: { isVisible: false },
          minimumDeliveryAmountRule: { isVisible: false },
        },

        packageOptions: {
          currentTab: "Selections",
        },
      },
      product: {},

      subtotal: "0.00",
      amountFromDelivery: "0.00",
      currentOrderMode: null,
    };
  },
  components: { VueSlickCarousel },
  computed: {
    ...mapGetters(["shoppingCartId", "shoppingCart"]),
  },
  async created() {
    log.info("Loading product", this.$route.params.id);
    if (!this.shoppingCart.shoppingCartId) {
      log.info("Shopping cart not exist, creating one");
      await this.$store.dispatch("shoppingCart/createShoppingCart");
    } else {
      log.info(`Shopping cart exists, ID=${this.shoppingCart.shoppingCartId}`);
      await this._loadShoppingCart();
    }
    await this.fetchData();
    this.subtotal = calculateSubtotal(this.shoppingCart.shoppingCart.lineItems);
    this.amountFromDelivery = (
      MINIMUM_DELIVERY_AMOUNT - Number(this.subtotal)
    ).toFixed(2);
    this.currentOrderMode = Cookies.get(
      "kingdynasty_ec_home_options_selected_mode"
    );
  },
  methods: {
    async fetchData() {
      this.display.isLoading = true;
      try {
        const product = await getById(this.$route.params.id);
        decorateProduct(product);
        if (!product.isDetailPageEnabled) {
          return this.$router.replace("/");
        }
        this._loadProductFromLineItem(product);
        this.product = product;
        log.info("Product info", product);

        // set default tab
        if (product.package.isPackage) {
          this.display.packageOptions.currentTab = "Selections";
        } else if (product.specification.isSpecificationEnabled) {
          this.display.packageOptions.currentTab = "Specifications";
        } else {
          this.display.packageOptions.currentTab = "Sub Menu";
        }
      } finally {
        this.display.isLoading = false;
      }
    },

    isSelectionDisabled(productSelection, productOption, lineItemSelection) {
      const isUnlimitedSelection =
        !productSelection.numberOfSelection ||
        Number(productSelection.numberOfSelection) === 0;
      if (isUnlimitedSelection) {
        return false;
      }
      const isMaxItemSelected =
        !lineItemSelection[productSelection.name].includes(productOption) &&
        lineItemSelection[productSelection.name].length >=
          Number(productSelection.numberOfSelection);
      return productSelection.isAllSelectedByDefault || isMaxItemSelected;
    },
    validateItemsAgainstSelection(product) {
      return (
        getAttributeValue(product.attributes, "packageOptions")
          .filter(
            (option) =>
              !!option.numberOfSelection && Number(option.numberOfSelection) > 0
          )
          .filter(
            (option) =>
              product.packageSelections[0][option.name].length !=
              Number(option.numberOfSelection)
          ).length === 0
      );
    },

    async onClickSaveDetailProductToCart(shouldGoCheckout = false) {
      this.display.isLoading = true;
      try {
        if (
          this.product.package.isPackage &&
          !this.validateItemsAgainstSelection(this.product)
        ) {
          log.info("Product is package but not all selection valid");
          return this.$message({
            type: "warning",
            message: "Please select for all sets.",
          });
        }
        if (this.product.specification.isSpecificationEnabled) {
          const isSpecValid =
            isNotNilOrEmpty(this.product.specificationSelectionIndex) &&
            this.product.specificationSelectionIndex >= 0;
          if (!isSpecValid) {
            log.info("Product has specification but no one is selected");
            return this.$message({
              type: "warning",
              message: "Please select a specification.",
            });
          }
        }
        const existingLineItem = this.shoppingCart.shoppingCart.lineItems.find(
          (item) => item.productId === this.product._id
        );
        const specSelection =
          this.product.specification.specificationOptions[
            this.product.specificationSelectionIndex
          ];
        let subItems = [];
        if (isNotNilOrEmpty(this.product.subProducts)) {
          // remove all existing sub items from cart
          const existingSubItemIndices =
            this.shoppingCart.shoppingCart.lineItems
              .filter((item) => item.parentProductId === this.product._id)
              .map((item) =>
                this.shoppingCart.shoppingCart.lineItems.findIndex(
                  (i) => item._id === i._id
                )
              )
              .filter((index) => index >= 0)
              .sort((i1, i2) => (i1 >= i2 ? -1 : 1));
          existingSubItemIndices.forEach((index) => {
            this.shoppingCart.shoppingCart.lineItems.splice(index, 1);
          });
          // add selected sub products to cart
          const selectedSubProducts = this.product.subProducts.filter(
            (subProduct) =>
              this.product.selectedSubProductNames.includes(
                `${subProduct.name} (${subProduct.unitNetPriceDisplay})`
              )
          );
          selectedSubProducts
            .map((subProduct) =>
              this.shoppingCart.shoppingCart.lineItems.findIndex(
                (item) => item.productId === subProduct._id
              )
            )
            .filter((index) => index >= 0)
            .forEach((index) => {
              this.shoppingCart.shoppingCart.lineItems.splice(index, 1);
            });
          subItems = selectedSubProducts.map((subProduct) => ({
            productId: subProduct._id,
            quantity: 1,
            parentProductId: this.product._id,
          }));
        }
        if (existingLineItem) {
          log.info("Product exists in cart, updating");
          setAttributeValue(
            existingLineItem.attributes,
            "packageSelections",
            this.product.packageSelections
          );
          setAttributeValue(
            existingLineItem.attributes,
            "specificationSelectionIndex",
            this.product.specificationSelectionIndex
          );
          setAttributeValue(
            existingLineItem.attributes,
            "specificationSelection",
            specSelection
          );
          const existingLineItemIndex =
            this.shoppingCart.shoppingCart.lineItems.findIndex(
              (item) => item.productId === existingLineItem.productId
            );
          subItems.forEach((subItem) => {
            this.shoppingCart.shoppingCart.lineItems.splice(
              existingLineItemIndex + 1,
              0,
              subItem
            );
          });
        } else {
          log.info("Product not exist in cart, adding");
          const newItem = {
            productId: this.product._id,
            quantity: 1,
            attributes: [
              ...this.product.attributes,
              {
                key: "packageSelections",
                value: this.product.packageSelections,
              },
              {
                key: "specificationSelectionIndex",
                value: this.product.specificationSelectionIndex,
              },
              {
                key: "specificationSelection",
                value: specSelection,
              },
            ],
          };
          this.shoppingCart.shoppingCart.lineItems.push(newItem);
          subItems.forEach((subItem) =>
            this.shoppingCart.shoppingCart.lineItems.push(subItem)
          );
        }
        if (this.product.isEventProduct) {
          // dine in for event product
          this.currentOrderMode = "DINE_IN";
          Cookies.set(
            "kingdynasty_ec_home_options_selected_mode",
            this.currentOrderMode
          );
        }
        await this._postAddLineItem(shouldGoCheckout);
      } catch (e) {
        log.error("Error when saving item to cart", e);
        this.$message({
          type: "error",
          message: "Failed to save to cart",
        });
      } finally {
        this.display.isLoading = false;
      }
    },

    async onChangeShoppingCartItemQuantity(event) {
      await this._changeShoppingCartItemQuantity(
        this.product,
        Number(event),
        true
      );
    },

    async onClickShoppingCart() {
      log.info("Go to shopping cart page");
      this.$router.push("/shopping-cart");
    },

    async onClickCheckout() {
      if (
        Number(this.amountFromDelivery) > 0 &&
        isDelivery(this.currentOrderMode)
      ) {
        log.info(`Cannot make delivery, current subtotal = ${this.subtotal}`);
        return this.$message(errorMessages.LESS_THAN_MINIMUM_DELIVERY_AMOUNT());
      }
      if (this.shoppingCart.shoppingCart.lineItems.length <= 0) {
        log.info("No shopping cart item");
        return this.$message({
          message:
            "Please consider to add some products to your shopping cart before checkout.",
          type: "error",
        });
      }
      this.display.isLoading = true;
      if (this.latestOrderId) {
        log.info(
          `Latest order ${this.latestOrderId}, fetching to check if same day dine-in`
        );
        const latestOrder = await getById(this.latestOrderId);
        latestOrder.createdTimeDisplayDateOnly = moment(
          latestOrder.createdTime
        ).format("HH:mm:ss, D MMM YYYY");
        log.info("Latest order", latestOrder);
        if (this._isSameDayOngoingDineInOrder(latestOrder)) {
          log.info(
            `Latest order ${this.latestOrderId} is non-finalised dine-in`
          );
          this.currentDineInOrder = latestOrder;
          this.display.isLoading = false;
          this.display.dialog.dineInAddLineItemsConfirmation.isVisible = true;
          return;
        }
      }
      this._goCheckout();
    },

    goHome() {
      log.info("Go home");
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push("/");
      }
    },

    async _postAddLineItem(shouldGoCheckout = false) {
      await this.$store.dispatch(
        "shoppingCart/updateShoppingCart",
        this.shoppingCart.shoppingCart
      );
      this.$message({
        type: "success",
        message: "Item saved to cart successfully.",
      });
      if (shouldGoCheckout) {
        return this._goCheckout();
      }
      this.$router.back();
    },
    async _loadShoppingCart() {
      await this.$store.dispatch("shoppingCart/loadShoppingCart");
      this.shoppingCart.shoppingCart.lineItems.forEach(
        decorateShoppingCartItem
      );
    },
    _loadProductFromLineItem(product) {
      const productIdShoppingCartItemQuantity =
        this.shoppingCart.shoppingCart.lineItems.reduce(
          (current, incoming) =>
            Object.assign(current, { [incoming.productId]: incoming.quantity }),
          {}
        );
      product.shoppingCartItemQuantity =
        productIdShoppingCartItemQuantity[product._id] || 0;
      (product.subProducts || []).forEach((subProduct) => {
        subProduct.shoppingCartItemQuantity =
          productIdShoppingCartItemQuantity[subProduct._id] || 0;
      });
      const existingItem = this.shoppingCart.shoppingCart.lineItems.find(
        (item) => item.productId === product._id
      );
      if (product.package.isPackage) {
        const productOptions = getAttributeValue(
          product.attributes,
          "packageOptions"
        );
        product.packageSelections = existingItem
          ? deepClone(existingItem.packageSelections)
          : [
              productOptions.reduce(
                (current, incoming) =>
                  Object.assign(current, {
                    [incoming.name]: [],
                  }),
                {}
              ),
            ];
        productOptions.forEach((opt) => {
          if (opt.isAllSelectedByDefault) {
            product.packageSelections[0][opt.name] = opt.options;
          }
        });
      }
      if (product.specification.isSpecificationEnabled) {
        product.specificationSelection = existingItem
          ? deepClone(existingItem.specificationSelection)
          : {};
        product.specificationSelectionIndex = existingItem
          ? existingItem.specificationSelectionIndex
          : null;
      }
      if (isNotNilOrEmpty(product.subProducts)) {
        product.selectedSubProductNames =
          this.shoppingCart.shoppingCart.lineItems
            .filter((item) => item.parentProductId === product._id)
            .map(
              (subItem) => `${subItem.name} (${subItem.unitNetPriceDisplay})`
            );
      }
    },
    async _changeShoppingCartItemQuantity(
      product,
      quantity,
      isUpdateShoppingCartImmediately
    ) {
      this.display.isLoading = true;
      try {
        product.shoppingCartItemQuantity = quantity;
        let shoppingCartItems = this.shoppingCart.shoppingCart.lineItems.filter(
          (item) => item.productId === product._id
        );

        const preparePackageSelections = (item) => {
          const productSelections = getAttributeValue(
            item.attributes,
            "packageOptions"
          );
          setAttributeValue(
            item.attributes,
            "packageSelections",
            Array(item.quantity)
              .fill(0)
              .map(() =>
                productSelections.reduce(
                  (current, incoming) =>
                    Object.assign(current, {
                      [incoming.name]: incoming.isAllSelectedByDefault
                        ? incoming.options
                        : [],
                    }),
                  {}
                )
              )
          );
        };
        if (shoppingCartItems.length > 0) {
          log.info(`Product ${product._id}, quantity ${quantity}`);
          shoppingCartItems.forEach((item) => {
            item.quantity = quantity;
            getAttributeValue(item.attributes, "isPackage") &&
              preparePackageSelections(item);
          });
        } else {
          log.info(`Adding product ${product._id} to shopping cart`);
          const newItem = {
            productId: product._id,
            quantity,
            attributes: product.attributes,
          };
          getAttributeValue(product.attributes, "isPackage") &&
            preparePackageSelections(newItem);
          this.shoppingCart.shoppingCart.lineItems.push(newItem);
        }
        this.shoppingCart.shoppingCart.lineItems =
          this.shoppingCart.shoppingCart.lineItems.filter(
            (item) => item.quantity > 0
          );
        if (isUpdateShoppingCartImmediately) {
          log.info(
            `Updating shopping cart ${this.shoppingCart.shoppingCartId} immediately`
          );
          await this.$store.dispatch(
            "shoppingCart/updateShoppingCart",
            this.shoppingCart.shoppingCart
          );
          await this._loadShoppingCart();
        }
        this.subtotal = calculateSubtotal(
          this.shoppingCart.shoppingCart.lineItems
        );
      } catch (err) {
        log.error(err);
        const { data, status } = err.response || {};
        if (status === 412) {
          // product sold out
          const { parameters } = data;
          this._refreshProductSoldOut(parameters);
          return this.$message({
            type: "error",
            message: "The product is sold out",
          });
        }
        this.$message({
          type: "error",
          message: `Unknown error (${err.message})`,
        });
      } finally {
        this.display.isLoading = false;
      }
    },
    _refreshProductSoldOut(soldOutProductIds) {
      this.products
        .filter((p) => soldOutProductIds.includes(p._id))
        .forEach((p) => {
          p.isSoldOut = true;
          setAttributeValue(p.attributes, "isSoldOut", true);
        });
    },
    _goCheckout() {
      log.info("Go to checkout page");
      this.$router.push("/order");
    },
  },
};
</script>

<style scoped>
.product__image {
  width: 100%;
}

.product__detail {
  padding: 0 0.5rem;
}

.product-list__item__shopping-cart-input {
  padding: 0;
  margin: 0;
}

.el-input-number.cart {
  width: 120px !important;
}

.el-input-number.cart >>> span {
  background-color: #221714 !important;
  border-radius: 0;
  height: 40px;
}

.el-input-number.cart >>> i {
  color: #c5b899 !important;
}

.el-input-number.cart >>> .el-input-number__decrease {
  left: 0 !important;
  top: 0 !important;
}

.el-input-number.cart >>> .el-input-number__increase {
  top: 0 !important;
}

.el-input-number.cart >>> .el-input-number__decrease.is-disabled,
.el-input-number.cart >>> .el-input-number__increase.is-disabled {
  opacity: 0.5;
}

.el-input-number.cart >>> input {
  width: 80px;
  border-color: #dcdfe6 !important;
  padding-left: 40px !important;
  padding-right: 0 !important;
}

.el-checkbox >>> .el-checkbox__label {
  color: #221714 !important;
}

.el-checkbox >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.el-radio >>> .el-radio__input.is-checked .el-radio__inner {
  background-color: #c5b899;
  border-color: #c5b899;
}

.el-checkbox >>> .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: #dcdfe6;
}

.el-checkbox.is-checked >>> .el-checkbox__label,
.el-radio.is-checked >>> .el-radio__label {
  color: #c5b899 !important;
}

.side-dialog-selection {
  padding-bottom: 1rem;
}

.side-dialog-selection >>> label.el-checkbox {
  display: block;
  margin-bottom: 0.5rem;
}

.side-dialog-selection >>> .el-checkbox__label {
  white-space: normal;
}

.side-dialog-selection .el-radio-group {
  width: 100%;
}

.side-dialog-selection .el-radio-group >>> .el-radio__label {
  display: inline-block;
  white-space: normal;
  line-height: 1.2rem;
}

.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 999;
}

.bottom-bar__col {
  padding: 0.5rem;
}

.back-button-wrapper {
  margin: 3rem 0 4rem 0;
}

.product-submenu {
  margin-bottom: 16px;
}

.product-submenu__item {
  margin-bottom: 8px;
}

.float-right {
  float: right;
}

.complex-product-details >>> .el-tabs__active-bar {
  background-color: #c5b899;
}

.complex-product-details >>> .el-tabs__item.is-active {
  color: #c5b899;
  font-weight: bold;
}

.product {
  margin-bottom: 5rem;
}
</style>
